import { ApiService } from '@/api';
import { cloneDeep } from 'lodash';
import { computed, ref } from 'vue';

type LocationList = Awaited<ReturnType<typeof ApiService.getLocations>>;
type LocationItem = LocationList[number];
export type Location = LocationItem & { children?: LocationList };

const data = ref<LocationList>([]);
const loaded = ref(false);

export function useLocation() {
    const update = (payload: any) => {
        data.value = payload;
    };
    const fetch = async () => {
        const locations = await ApiService.getLocations({});
        data.value = locations;
    };

    const tree = (items: Location[] = []): Location[] => {
        const root = [];
        const lookup: any = {};
        for (const item of items) {
            const id = item.id;
            const parentId = item.parentId;

            if (!lookup[id]) lookup[id] = { ['children']: [] };

            lookup[id] = { ...item, ['children']: lookup[id]['children'] };

            const TreeItem = lookup[id];

            if (!parentId) {
                root.push(TreeItem);
            } else {
                if (!lookup[parentId]) lookup[parentId] = { ['children']: [] };

                lookup[parentId]['children'].push(TreeItem);
            }
        }
        return root;
    };

    const locations = computed(() => {
        return tree(cloneDeep(data.value || []));
    });

    const states = computed(() => {
        const items = (data.value || []).filter((x) => x.type == 'state');
        return tree(cloneDeep(items));
    });

    const districts = computed(() => {
        const items = (data.value || []).filter((x) => ['state', 'district'].includes(x.type));
        return tree(cloneDeep(items));
    });

    const areas = computed(() => {
        const items = (data.value || []).filter((x) => ['state', 'district', 'area'].includes(x.type));
        return tree(cloneDeep(items));
    });

    const pincodes = computed(() => {
        const pincodes = (data.value || []).filter((x) => x.type == 'pincode');
        const areas = (data.value || []).filter((x) => x.type == 'area');
        const districts = (data.value || []).filter((x) => x.type == 'district');
        const states = (data.value || []).filter((x) => x.type == 'state');
        return pincodes.map((p) => {
            let area = areas.find((a) => a.id == p.parentId);
            let district = area ? districts.find((d) => d.id == area.parentId) : null;
            let state = district ? states.find((s) => s.id == district.parentId) : null;
            return {
                pincode: p.title,
                area: area?.title,
                district: district?.title,
                state: state?.title,
            };
        });
    });

    const getLocation = (id: number) => {
        return data.value?.find((x) => x.id == id);
    };

    return {
        locations,
        getLocation,
        states,
        districts,
        areas,
        pincodes,
        update,
        fetch,
        loaded: computed(() => loaded.value),
    };
}
